/**
 * Viewport height utility
 *
 * Single source of truth for the `--app-height` custom property that
 * `body.app-layout` (src/index.css) locks html/body/#root to.
 *
 * Safari PWAs can report stale values for 100dvh and -webkit-fill-available
 * after the virtual keyboard dismisses. window.innerHeight is always correct,
 * so we use it as the source of truth for the app layout height.
 *
 * This lives here rather than inside BottomNavigation so that the native
 * Capacitor keyboard listeners (src/services/capacitor/index.ts) can re-sync it
 * without importing a React component.
 */

/** Write the current visible viewport height to `--app-height`. */
export function setAppHeight(): void {
  document.documentElement.style.setProperty('--app-height', `${window.innerHeight}px`);
}

/**
 * Undo a document scroll offset left behind by the keyboard.
 *
 * On AppLayout pages the document itself never scrolls — `body.app-layout` is
 * `overflow: hidden` and all scrolling happens inside `<main>`. So any non-zero
 * `window.scrollY` there is residue from iOS scrolling the WebView to reveal a
 * focused input and not restoring it, which reads as a gap above the header.
 * Standalone pages (login, signup, landing) do scroll the document, so they are
 * deliberately left alone.
 */
export function resetStrayDocumentScroll(): void {
  if (!document.body.classList.contains('app-layout')) return;
  if (window.scrollY === 0) return;
  window.scrollTo(0, 0);
}

/**
 * How many pixels of the *layout* viewport the on-screen keyboard covers.
 *
 * This is the number a `position: fixed; bottom: 0` element has to be lifted
 * by to sit flush on top of the keyboard — and it is deliberately NOT the
 * keyboard's height:
 *
 *  - Native Capacitor (`Keyboard.resize: 'native'`) shrinks the WebView frame
 *    itself, and Android Chrome does the same via
 *    `interactive-widget=resizes-content` (index.html). The layout viewport
 *    bottom already IS the top of the keyboard, so the correct lift is 0.
 *    Adding `--keyboard-height` there would double-count it.
 *  - iOS Safari (and any browser that only shrinks the visual viewport) leaves
 *    `window.innerHeight` at its full value, so the keyboard genuinely overlaps
 *    the layout viewport and the difference below is its height.
 */
export function getKeyboardOverlap(): number {
  const viewport = window.visualViewport;
  if (!viewport) return 0;
  return Math.max(0, Math.round(window.innerHeight - viewport.height - viewport.offsetTop));
}
